import React, { useState } from "react";
import AppLogo from "../../components/ui/AppLogo";
import AppIcon from "../../components/ui/AppIcon";
import api from "../../services/api";

// Portal base (used only for the "Forgot password?" link, which reuses the
// portal's existing reset flow). Derived from the configured login URL.
const PORTAL_LOGIN_URL =
  process.env.REACT_APP_LOGIN_URL || "http://localhost:5555/signin";
const FORGOT_PASSWORD_URL = PORTAL_LOGIN_URL.replace(/\/signin\/?$/, "/forgot-password");

/**
 * SignInPage
 *
 * Self-contained login for the Intercom (chat) app. Calls the SAME backend
 * endpoint the portal uses (`POST /auth/login` → { accessToken, refreshToken,
 * user }), persists the tokens exactly like the portal-redirect flow does, then
 * reloads into the app so every provider re-initialises with the new session.
 */
export default function SignInPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");

    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setSubmitting(true);
    try {
      // api response interceptor returns response.data directly.
      const data = await api.post("auth/login", {
        email: email.trim(),
        password,
      });

      const accessToken = data?.accessToken;
      const refreshToken = data?.refreshToken;
      const user = data?.user;

      if (!accessToken) {
        throw new Error("No access token returned");
      }

      localStorage.setItem("auth_token", accessToken);
      if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
      if (user) localStorage.setItem("chat_user", JSON.stringify(user));

      // Reload into the app — AuthContext validates the stored token and every
      // provider (Chat/Socket) re-initialises cleanly for the new session.
      window.location.replace("/chat-interface");
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Invalid email or password. Please try again.";
      setError(typeof msg === "string" ? msg : "Sign in failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-app-bg px-4 py-10 font-sans">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="flex flex-col items-center mb-8">
          <AppLogo size={40} showWordmark />
        </div>

        {/* Card */}
        <form
          onSubmit={handleSubmit}
          className="bg-app-surface border border-border-subtle rounded-3xl shadow-premium p-6 sm:p-7"
        >
          {error && (
            <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-error/10 border border-error/20 text-error text-[13px] font-medium animate-fade-in">
              <AppIcon name="ExclamationTriangleIcon" size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Email */}
          <label className="block mb-4">
            <span className="text-[12px] font-bold uppercase tracking-widest text-text-muted">
              Email
            </span>
            <div className="mt-1.5 relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted">
                <AppIcon name="EnvelopeIcon" size={17} />
              </span>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-app-bg border border-border-subtle text-[14px] text-text-primary placeholder:text-text-muted outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/5 transition-all"
              />
            </div>
          </label>

          {/* Password */}
          <label className="block mb-5">
            <span className="text-[12px] font-bold uppercase tracking-widest text-text-muted">
              Password
            </span>
            <div className="mt-1.5 relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted">
                <AppIcon name="LockClosedIcon" size={17} />
              </span>
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-10 pr-11 py-3 rounded-xl bg-app-bg border border-border-subtle text-[14px] text-text-primary placeholder:text-text-muted outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/5 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-black/5 transition-all"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                <AppIcon name={showPassword ? "EyeSlashIcon" : "EyeIcon"} size={17} />
              </button>
            </div>
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-xl bg-primary text-white text-sm font-bold shadow-premium hover:bg-primary-dark active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </button>

          <div className="mt-4 text-center">
            <a
              href={FORGOT_PASSWORD_URL}
              className="text-[13px] font-semibold text-primary hover:text-primary-dark transition-colors"
            >
              Forgot password?
            </a>
          </div>
        </form>

        <p className="mt-6 text-center text-[11px] text-text-muted">
          © 2026 Sapient Codelabs
        </p>
      </div>
    </div>
  );
}
